"use client";

import { SheetCartItem } from "@/components/molecules/Cart/sheet-cart-item";
import Pagination from "@/components/ui/pagination";
import { ITEMS_PER_PAGE, useCartProducts } from "@/hooks/useCartProducts";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Link from "next/link";

type CartSheetProductsProps = {
  locale: Locales;
};

const CartSheetProducts = ({ locale }: CartSheetProductsProps) => {
  const {
    itemCount,
    shouldShowLoader,
    currentPageItems,
    currentPage,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    goToNextPage,
    goToPrevPage,
    goToPage,
  } = useCartProducts(locale);

  if (shouldShowLoader) {
    return (
      <div className="flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (itemCount === 0) {
    return (
      <>
        <h2 className="sm:font-cabinet-grotesk text-lg font-medium max-md:mt-4 sm:text-2xl">
          {__("No items in cart", locale)}
        </h2>
        <p className="font-cabinet-grotesk -mt-2 flex items-center gap-1 text-lg">
          {__("Explore our", locale)}{" "}
          <Link
            href={getPermalink(locale, "catalogue")}
            className="text-pink-primary link-effect relative flex items-center font-medium transition-all duration-300 ease-in-out hover:scale-[102%]"
          >
            {__("catalogue", locale)} <ArrowUpRight />
          </Link>
        </p>
      </>
    );
  }

  return (
    <>
      {currentPageItems.map((item) => (
        <li key={item.cartItem.id}>
          <SheetCartItem
            locale={locale}
            cartItem={item.cartItem}
            prismicProduct={item.prismicProduct}
            medusaProduct={item.product}
            quantity={item.cartItem.quantity}
          />
        </li>
      ))}
      {totalPages > 1 && (
        <Pagination
          showPaginationLabels={false}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          goToPrevPage={goToPrevPage}
          goToNextPage={goToNextPage}
          startIndex={startIndex}
          endIndex={endIndex}
          locale={locale}
          goToPage={goToPage}
          totalPages={totalPages}
        />
      )}
    </>
  );
};

export default CartSheetProducts;
